import React, { useMemo, useState } from 'react'
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Checkbox,
  Paper,
  Button,
  TextField,
} from '@mui/material'
import { Assignment, Search } from '@mui/icons-material'
import {
  MaterialReactTable,
  MRT_ColumnDef,
  useMaterialReactTable,
} from 'material-react-table'
import { getDefaultMRTOptions } from 'src/utils/getDefaultMRTOptions'

import WorkItemStatusChip from 'src/components/WorkItemStatusChip'

type WorkItem = {
  id: string
  title: string
  description: string | null
  status: string
  estimatedHours: number | null
  actualHours: number | null
  trackedBy: string | null
  assignee: {
    id: string
    firstName: string
    lastName: string
  } | null
  trackedByComponent: {
    id: string
    name: string
    partNumber: string | null
    serialNumber: string | null
    description: string | null
  } | null
}

type WorkItemsTabbedTableProps = {
  workItems: WorkItem[]
  selectedWorkItems: string[]
  onWorkItemSelectionChange: (workItemId: string, selected: boolean) => void
  onBulkOperationsClick: () => void
}

const WorkItemsTabbedTable: React.FC<WorkItemsTabbedTableProps> = ({
  workItems,
  selectedWorkItems,
  onWorkItemSelectionChange,
  onBulkOperationsClick,
}) => {
  const [activeTab, setActiveTab] = useState(0)

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
  }

  // Filter work items based on active tab
  const filteredWorkItems = useMemo(() => {
    switch (activeTab) {
      case 0: // All
        return workItems
      case 1: // In Progress
        return workItems.filter((item) => item.status === 'IN_PROGRESS')
      case 2: // Complete
        return workItems.filter((item) => item.status === 'COMPLETED')
      default:
        return workItems
    }
  }, [workItems, activeTab])

  // Get counts for each tab
  const tabCounts = useMemo(() => {
    const allCount = workItems.length
    const inProgressCount = workItems.filter(
      (item) => item.status === 'IN_PROGRESS'
    ).length
    const completedCount = workItems.filter(
      (item) => item.status === 'COMPLETED'
    ).length

    return [allCount, inProgressCount, completedCount]
  }, [workItems])

  const defaultTableOptions = getDefaultMRTOptions<any>()

  const columns = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        id: 'selection',
        header: '',
        size: 50,
        Cell: ({ row }) => (
          <Checkbox
            checked={selectedWorkItems.includes(row.original.id)}
            onChange={(e) => {
              onWorkItemSelectionChange(row.original.id, e.target.checked)
            }}
          />
        ),
      },
      {
        id: 'title',
        header: 'Task',
        size: 400,
        Cell: ({ row }) => (
          <Box>
            <Typography variant="subtitle2" fontWeight="medium">
              {row.original.title}
            </Typography>
            {row.original.description && (
              <Typography variant="body2" color="textSecondary">
                {row.original.description}
              </Typography>
            )}
          </Box>
        ),
      },
      {
        id: 'estimatedHours',
        header: 'Est. Hours',
        size: 100,
        Cell: ({ row }) => (
          <Typography variant="body2">
            {row.original.estimatedHours?.toFixed(2) || '0.00'}
          </Typography>
        ),
      },
      {
        id: 'trackedBy',
        header: 'Tracked By',
        size: 150,
        Cell: ({ row }) => (
          <Box display="flex" alignItems="center" gap={1}>
            {row.original.trackedByComponent ? (
              <Typography variant="body2">
                {row.original.trackedByComponent.name}
              </Typography>
            ) : (
              <Typography variant="body2" color="textSecondary">
                Not specified
              </Typography>
            )}
          </Box>
        ),
      },
      {
        id: 'status',
        header: 'Remaining',
        size: 120,
        Cell: ({ row }) => <WorkItemStatusChip status={row.original.status} />,
      },
      {
        id: 'related',
        header: 'Related',
        size: 80,
        Cell: ({ row }) => (
          <Box display="flex" justifyContent="center">
            <Typography variant="body2" color="warning.main">
              ⚠️
            </Typography>
          </Box>
        ),
      },
    ],
    [selectedWorkItems, onWorkItemSelectionChange]
  )

  const table = useMaterialReactTable({
    ...defaultTableOptions,
    columns,
    data: filteredWorkItems,
    enableTopToolbar: false,
    enableColumnFilters: false,
    enablePagination: true,
    muiTablePaperProps: {
      elevation: 0,
      className: 'border border-solid border-gray-200 w-full',
    },
    muiTableContainerProps: {
      className: 'overflow-auto',
    },
    defaultColumn: {
      size: 'auto',
    },
    state: {
      columnPinning: {
        left: ['selection'],
      },
    },
  })

  return (
    <Box>
      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <span>All</span>
                <Box
                  sx={{
                    backgroundColor:
                      activeTab === 0 ? 'primary.main' : 'grey.300',
                    color: activeTab === 0 ? 'white' : 'text.primary',
                    borderRadius: '50%',
                    width: 20,
                    height: 20,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    fontWeight: 'medium',
                  }}
                >
                  {tabCounts[0]}
                </Box>
              </Box>
            }
          />
          <Tab
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <span>In Progress</span>
                <Box
                  sx={{
                    backgroundColor:
                      activeTab === 1 ? 'primary.main' : 'grey.300',
                    color: activeTab === 1 ? 'white' : 'text.primary',
                    borderRadius: '50%',
                    width: 20,
                    height: 20,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    fontWeight: 'medium',
                  }}
                >
                  {tabCounts[1]}
                </Box>
              </Box>
            }
          />
          <Tab
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <span>Complete</span>
                <Box
                  sx={{
                    backgroundColor:
                      activeTab === 2 ? 'primary.main' : 'grey.300',
                    color: activeTab === 2 ? 'white' : 'text.primary',
                    borderRadius: '50%',
                    width: 20,
                    height: 20,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    fontWeight: 'medium',
                  }}
                >
                  {tabCounts[2]}
                </Box>
              </Box>
            }
          />
        </Tabs>
      </Box>

      {/* Search and Actions Bar */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Box sx={{ width: 300 }}>
          <TextField
            size="small"
            placeholder="Search"
            InputProps={{
              startAdornment: <Search fontSize="small" />,
            }}
            sx={{ width: '100%' }}
          />
        </Box>
        <Box display="flex" gap={1}>
          <Button variant="outlined" size="small">
            More Actions
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={onBulkOperationsClick}
            disabled={selectedWorkItems.length === 0}
            startIcon={<Assignment />}
          >
            + Add
          </Button>
        </Box>
      </Box>

      {/* Table */}
      <MaterialReactTable table={table} />
    </Box>
  )
}

export default WorkItemsTabbedTable
